import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Modal, Form, Checkbox, Alert, Select } from '../../';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useGetCloudAccountsQuery, useGetMoveGroupsQuery } from '../../../api';
import { isApplianceReady, makeNiceName, vmLevelMigrationsEnabled } from '../../../lib/helpers';
import { NIL_UUID } from '../../../constants';
import MoveGroupSelector from '../../MoveGroupSelector';
import VSphereFilterSelector, { clearVSphereCache } from './VSphereFilterSelector';
import { WarningModal } from '../WarningModal';

import './RefreshSourcesModal.scss';

const RefreshSourcesModal = ({ open, onClose, onSuccess, userOrg }) => {
    const [form] = Form.useForm();
    const [selectedApplianceId, setSelectedApplianceId] = useState(null);
    const [selectedCloudAccountId, setSelectedCloudAccountId] = useState(null);
    const [noFilter, setNoFilter] = useState(true);
    const [downloadCSV, setDownloadCSV] = useState(false);
    const [moveGroupData, setMoveGroupData] = useState(null);
    const [filterParams, setFilterParams] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showAllWarning, setShowAllWarning] = useState(false);
    const vsphereCleanupRef = useRef(null);

    // Fetch cloud accounts
    const { data: cloudAccountsData, isLoading: isLoadingAccounts } = useGetCloudAccountsQuery({
        userOrg,
        size: 500,
        sort: 'name,asc',
    });

    // Fetch move groups
    const { data: moveGroupsData } = useGetMoveGroupsQuery(userOrg);
    const moveGroups = moveGroupsData?.content || [];

    const vsphereCloudAppliances = useMemo(() => {
        if (!cloudAccountsData?.content) return [];
        return cloudAccountsData.content;
    }, [cloudAccountsData]);

    // Check if only one appliance is available
    const hasOnlyOneAppliance = useMemo(() => vsphereCloudAppliances.length === 1, [vsphereCloudAppliances]);

    // Auto-select single appliance
    useEffect(() => {
        if (hasOnlyOneAppliance && !selectedApplianceId) {
            const appliance = vsphereCloudAppliances[0];
            if (isApplianceReady(appliance.appliance)) {
                setSelectedApplianceId(appliance.appliance.id);
                setSelectedCloudAccountId(appliance.id);
                form.setFieldsValue({ applianceSelector: appliance.id });
            }
        }
    }, [hasOnlyOneAppliance, vsphereCloudAppliances, selectedApplianceId, form]);

    const handleApplianceChange = useCallback(
        (cloudAccountId) => {
            const selectedAccount = vsphereCloudAppliances.find((acc) => acc.id === cloudAccountId);
            if (selectedAccount) {
                setSelectedApplianceId(selectedAccount.appliance?.id || null);
                setSelectedCloudAccountId(cloudAccountId);

                // Reset filter when appliance changes
                if (!noFilter) {
                    setNoFilter(true);
                    setFilterParams(null);
                }

                // Reset component state (but keep cache for quick switching)
                if (vsphereCleanupRef.current) {
                    vsphereCleanupRef.current();
                }
            }
        },
        [vsphereCloudAppliances, noFilter],
    );

    const handleNoFilterChange = useCallback((e) => {
        const checked = e.target.checked;
        setNoFilter(checked);
        if (checked) {
            setFilterParams(null);
            // Reset component state when switching to "All" (but keep cache)
            if (vsphereCleanupRef.current) {
                vsphereCleanupRef.current();
            }
        }
    }, []);

    const handleFilterParamsChange = useCallback((params) => {
        setFilterParams(params);
    }, []);

    const handleSubmit = useCallback(async () => {
        try {
            setIsSubmitting(true);

            // Build refresh params
            const refreshSourcesParams = {
                applianceId: selectedApplianceId,
                cloudAccountId: selectedCloudAccountId,
                downloadCSV,
            };

            // Add filter params if not "All"
            if (!noFilter && filterParams) {
                refreshSourcesParams.datacenter_name = filterParams.datacenter_name;
                refreshSourcesParams.cluster_name = filterParams.cluster_name;
                refreshSourcesParams.resource_pool = filterParams.resource_pool;
                refreshSourcesParams.folder_name = filterParams.folder_name;
            }

            // Add move group info if selected
            if (moveGroupData) {
                if (moveGroupData.mode === 'new' && moveGroupData.newMoveGroupName) {
                    refreshSourcesParams.move_group_id = NIL_UUID;
                    refreshSourcesParams.move_group_name = moveGroupData.newMoveGroupName;
                    if (moveGroupData.targetMigrationDate) {
                        refreshSourcesParams.target_migration_date =
                            moveGroupData.targetMigrationDate.format('YYYY-MM-DD');
                    }
                    if (moveGroupData.assignedResourceId) {
                        refreshSourcesParams.assigned_resource_email = moveGroupData.assignedResourceId;
                    }
                } else if (moveGroupData.mode === 'existing' && moveGroupData.selectedMoveGroupId) {
                    if (moveGroupData.selectedMoveGroupId !== NIL_UUID) {
                        refreshSourcesParams.move_group_id = moveGroupData.selectedMoveGroupId;
                        // Look up the move group name from the move groups list
                        const selectedMoveGroup = moveGroups.find((mg) => mg.id === moveGroupData.selectedMoveGroupId);
                        if (selectedMoveGroup) {
                            refreshSourcesParams.move_group_name = selectedMoveGroup.name || '';
                        }
                    }
                }
            }

            // Call success handler with params
            onSuccess?.(refreshSourcesParams);
            handleClose();
        } catch (error) {
            console.error('Validation failed:', error);
        } finally {
            setIsSubmitting(false);
        }
    }, [
        selectedApplianceId,
        selectedCloudAccountId,
        noFilter,
        filterParams,
        moveGroupData,
        downloadCSV,
        onSuccess,
        moveGroups,
    ]);

    const handleConfirm = useCallback(() => {
        if (noFilter) {
            setShowAllWarning(true);
        } else {
            handleSubmit();
        }
    }, [noFilter, handleSubmit]);

    const handleAllWarningCancel = useCallback(() => {
        setShowAllWarning(false);
    }, []);

    const handleClose = useCallback(() => {
        form.resetFields();
        setSelectedApplianceId(null);
        setSelectedCloudAccountId(null);
        setNoFilter(true);
        setDownloadCSV(false);
        setMoveGroupData(null);
        setFilterParams(null);
        setIsSubmitting(false);
        setShowAllWarning(false);

        // Clear vSphere global cache when modal closes
        clearVSphereCache();
        onClose?.();
    }, [form, onClose]);

    const isApplianceSelected = !!selectedApplianceId;
    const canSubmit = isApplianceSelected && (!noFilter ? !!filterParams : true);

    return (
        <>
            <Modal
                title="Select a Migration Appliance to refresh vSphere sources"
                open={open}
                onOk={handleConfirm}
                onCancel={handleClose}
                okText="Confirm"
                confirmLoading={isSubmitting}
                width={700}
                maskClosable={false}
                okButtonProps={{ disabled: !canSubmit }}
            >
                <Form form={form} layout="vertical">
                    {/* No Appliances Message */}
                    {!isLoadingAccounts && vsphereCloudAppliances.length === 0 && (
                        <Alert
                            title='There are currently no migration appliances suitable to perform migrations by VM. Please set up an appliance and then click "Refresh vSphere Sources" button to refresh the source inventory.'
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {/* Appliance Selector */}
                    {vsphereCloudAppliances.length > 0 && (
                        <>
                            <Form.Item
                                label="Select an Appliance"
                                name="applianceSelector"
                                rules={[{ required: true, message: 'Please select an appliance' }]}
                            >
                                <Select
                                    placeholder="Select an Appliance"
                                    onChange={handleApplianceChange}
                                    loading={isLoadingAccounts}
                                    disabled={hasOnlyOneAppliance}
                                    options={vsphereCloudAppliances
                                        .filter((cloud_account) => vmLevelMigrationsEnabled(cloud_account))
                                        .map((cloud_account) => ({
                                            value: cloud_account.id,
                                            label: `${cloud_account.name}${` (${makeNiceName(cloud_account.type, true)})`}`,
                                            disabled: !isApplianceReady(cloud_account.appliance),
                                        }))
                                        ?.sort(
                                            (a, b) =>
                                                a.disabled - b.disabled ||
                                                a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
                                        )}
                                />
                            </Form.Item>

                            {/* Warning Message */}
                            {isApplianceSelected && (
                                <Alert
                                    title={
                                        <span>
                                            <ExclamationCircleOutlined /> Please note: manual refresh of the page is
                                            required to see the new sources.
                                        </span>
                                    }
                                    type="warning"
                                    showIcon={false}
                                    style={{ marginBottom: 16 }}
                                />
                            )}

                            {/* Filter Options */}
                            {isApplianceSelected && (
                                <div className="additional-info-section">
                                    <Form.Item>
                                        <Checkbox checked={noFilter} onChange={handleNoFilterChange}>
                                            All
                                        </Checkbox>
                                    </Form.Item>

                                    {/* vSphere Filter Selector */}
                                    {!noFilter && selectedApplianceId && (
                                        <VSphereFilterSelector
                                            applianceId={selectedApplianceId}
                                            onFilterChange={handleFilterParamsChange}
                                            userOrg={userOrg}
                                            onCleanupRef={vsphereCleanupRef}
                                        />
                                    )}

                                    {/* Download CSV Checkbox */}
                                    <Form.Item>
                                        <Checkbox
                                            checked={downloadCSV}
                                            onChange={(e) => setDownloadCSV(e.target.checked)}
                                        >
                                            Download Migrations CSV upon completion
                                        </Checkbox>
                                    </Form.Item>

                                    {/* Move Group Selector */}
                                    <Form.Item label="Application or Move Group Name (optional)">
                                        <MoveGroupSelector
                                            value={moveGroupData}
                                            onChange={setMoveGroupData}
                                            userOrg={userOrg}
                                            disabled={isSubmitting}
                                        />
                                    </Form.Item>
                                </div>
                            )}
                        </>
                    )}
                </Form>
            </Modal>

            <WarningModal open={showAllWarning} onOk={handleSubmit} onCancel={handleAllWarningCancel}>
                Refreshing all sources may take a significant amount of time.
            </WarningModal>
        </>
    );
};

export default RefreshSourcesModal;
